/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

import Fuse from "fuse.js";
import merge from "deepmerge";
import Logger from "../../../clients/logger";
import { BootTaskCallbackInterface } from "../types";
import SearchClient from "../../../clients/searchClient";
import LauncherClient from "../../../clients/launcherClient";
import ConfigClient from "../../../clients/configClient";

/**
 * Boot task that registers a component launcher as search provider with the search client. This way, people can search and we can return a list of components that they can spawn.
 * Can assume LauncherClient is ready when this task runs.
 * @param {*} cb
 * @private
 */
export class SetupSearchLauncherTask {
	/**
	 * Starts boot task for setting up search launcher
	 * @param doneCallback
	 */
	public async start(doneCallback: BootTaskCallbackInterface) {
		Logger.system.debug("setupSearchLauncherTask start");

		const { data: servicesConfig } = await ConfigClient.getValue({
			field: "finsemble.servicesConfig",
		});

		/**
		 * Register Search here
		 * We'll look into the config for the provider callback component and it's title
		 */
		let providerAction: {
			title: string | null;
			providerActionComponent: string | null;
		} = {
			title: null,
			providerActionComponent: null,
		};
		if (servicesConfig && servicesConfig.launcher && servicesConfig.launcher.searchProviderAction) {
			providerAction.title = servicesConfig.launcher.searchProviderAction.title;
			providerAction.providerActionComponent = servicesConfig.launcher.searchProviderAction.component;
		}

		SearchClient.register(
			{
				name: "Installed Components",
				searchCallback: this.searchComponents.bind(this),
				itemActionCallback: (params, searchResultsWindowName) => {
					if (!params.action || !params.item || params.action.name !== "Spawn") return;
					LauncherClient.spawn(params.item.name, {
						addToWorkspace: true,
						monitor: "mine",
						relativeWindow: { windowName: searchResultsWindowName },
					});
				},
				providerActionTitle: providerAction.title,
				providerActionCallback: (searchResultsWindowName) => {
					Logger.system.debug("setupSearchLauncherTask providerActionCallback", searchResultsWindowName);
					if (providerAction.providerActionComponent) {
						LauncherClient.spawn(providerAction.providerActionComponent, {
							addToWorkspace: true,
							monitor: "mine",
							relativeWindow: { windowName: searchResultsWindowName },
						});
					}
				},
			},
			() => {
				doneCallback("setupSearchLauncherTask", "bootTasks", "completed");
			}
		);
	}

	/**
	 *Returns a list of installed components based off of a search string.
	 *@param {object} params
	 *@param {string} params.text The search text
	 *
	 */
	private async searchComponents(params, cb) {
		let options: Fuse.IFuseOptions<any> = {
			shouldSort: true,
			includeScore: true,
			includeMatches: true,
			threshold: 0.4,
			location: 0,
			distance: 100,
			minMatchCharLength: 1,
			keys: ["component.displayName", "component.type"],
		};

		const { data: configComponentSearch } = await ConfigClient.getValue({
			field: "finsemble.servicesConfig.launcher.search",
		});

		// Check if this search provider is enabled, and merge configuration with defaults
		if (configComponentSearch?.enabled === false) {
			cb(null, []);
			return;
		}
		if (configComponentSearch?.options) {
			options = merge(options, configComponentSearch.options);
		}

		const { err, data: apps } = await LauncherClient.getComponentList();

		if (err || !apps) {
			cb(null, []);
			return;
		}

		const appsAsArray = Object.values(apps) ?? [];

		// Default is all apps
		const defaultResults = appsAsArray.map((app) => {
			return {
				item: app,
				score: 100,
				matches: 1,
			};
		});

		// Fuse is a 3rd party search library. We pass in the search string and search on component type
		const getFuseResults = () => {
			const fuse = new Fuse(appsAsArray, options);
			return fuse.search(params.text);
		};

		// Fuse doesn't allow wildcard or empty search string, so when we don't have a search string we return all apps
		const fuseResults = params.text.length ? getFuseResults() : defaultResults;

		// Filter out apps that aren't launchable, and then format the results to the Search API specification
		const searchResults = fuseResults.flatMap((result) => {
			const { item } = result;
			const componentConfig = item.component;
			const entry = {
				name: componentConfig.type,
				displayName: componentConfig.displayName || componentConfig.type,
				score: result.score,
				matches: result.matches,
				icon: item.icons?.toolbarIcon,
				type: "Application",
				description: "",
				actions: [{ name: "Spawn" }],
				tags: [],
			};
			const launchableByUser = result.item.foreign?.components?.["App Launcher"]?.launchableByUser;
			return launchableByUser ? [entry] : [];
		});
		cb(null, searchResults);
	}
}
